/**
 * Search-condition fields for the 'search' analytics event (dates, categories,
 * usage). Pure and server-safe: recordSearchEvent normalizes whatever the
 * client sent through this, so stored metadata is always well-formed.
 */

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const MAX_CATEGORIES = 10;
const MAX_CATEGORY_LENGTH = 40;
const USAGE_VALUES = ["pickup", "shipping", "delivery"] as const;

export type SearchUsage = (typeof USAGE_VALUES)[number];

export interface SearchMetaInput {
  checkin?: string | null;
  checkout?: string | null;
  categories?: readonly unknown[] | null;
  usage?: string | null;
}

export interface SearchMetaFields {
  checkin: string | null;
  checkout: string | null;
  nights: number | null;
  lead_days: number | null;
  categories: string[];
  usage: SearchUsage | null;
}

/** 'YYYY-MM-DD' → UTC midnight ms, or null when malformed / not a real date. */
function parseDay(value: string | null | undefined): number | null {
  if (typeof value !== "string" || !DATE_PATTERN.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const ms = Date.UTC(y, m - 1, d);
  const check = new Date(ms);
  if (
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() !== m - 1 ||
    check.getUTCDate() !== d
  ) {
    return null;
  }
  return ms;
}

/** Today's date in KST as UTC midnight ms. */
function kstToday(now: Date): number {
  const kst = new Date(now.getTime() + KST_OFFSET_MS);
  return Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth(), kst.getUTCDate());
}

function normalizeCategories(input: readonly unknown[] | null | undefined): string[] {
  if (!Array.isArray(input)) return [];
  const out: string[] = [];
  for (const raw of input) {
    if (typeof raw !== "string") continue;
    const code = raw.trim();
    if (!code || code.length > MAX_CATEGORY_LENGTH || out.includes(code)) continue;
    out.push(code);
    if (out.length >= MAX_CATEGORIES) break;
  }
  return out;
}

export function buildSearchMetaFields(
  input: SearchMetaInput,
  now: Date = new Date(),
): SearchMetaFields {
  const checkinMs = parseDay(input.checkin);
  const checkoutMs = parseDay(input.checkout);
  const nights =
    checkinMs !== null && checkoutMs !== null && checkoutMs >= checkinMs
      ? Math.round((checkoutMs - checkinMs) / DAY_MS)
      : null;
  const leadDays =
    checkinMs !== null ? Math.round((checkinMs - kstToday(now)) / DAY_MS) : null;
  const usage = (USAGE_VALUES as readonly string[]).includes(input.usage ?? "")
    ? (input.usage as SearchUsage)
    : null;

  return {
    checkin: checkinMs !== null ? (input.checkin as string) : null,
    checkout: checkoutMs !== null ? (input.checkout as string) : null,
    nights,
    lead_days: leadDays,
    categories: normalizeCategories(input.categories),
    usage,
  };
}

/** True when the user set any search condition besides place/keyword. */
export function hasSearchConditions(input: SearchMetaInput): boolean {
  const f = buildSearchMetaFields(input);
  return Boolean(f.checkin || f.checkout || f.categories.length || f.usage);
}

/** "10/12~10/14" (or "10/12" for a single day); null without a valid checkin. */
export function formatSearchDateLabel(
  checkin: string | null | undefined,
  checkout: string | null | undefined,
): string | null {
  const a = parseDay(checkin);
  if (a === null) return null;
  const md = (ms: number) => {
    const d = new Date(ms);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  };
  const b = parseDay(checkout);
  return b === null || b === a ? md(a) : `${md(a)}~${md(b)}`;
}

/** Maps search-URL filters (SearchUrlState["filters"]) to recordSearchEvent fields. */
export function searchConditionsFromFilters(
  filters:
    | {
        checkin?: string;
        checkout?: string;
        categories?: readonly string[];
        usageMethod?: string;
      }
    | null
    | undefined,
): {
  checkin: string | null;
  checkout: string | null;
  categories: readonly string[];
  usage: string | null;
} {
  return {
    checkin: filters?.checkin ?? null,
    checkout: filters?.checkout ?? null,
    categories: filters?.categories ?? [],
    usage: filters?.usageMethod ?? null,
  };
}
